import { c as _c } from "react/compiler-runtime";
// «Чаты» покупателя — 1:1 по макету «Чаты» (Figma ЛК Покупателя, кадры 1200).
// Слева список тредов (367px, строки 74px), справа открытый тред (шапка 60,
// лента на #F8F8F8, композер 64). Сверху — чипы «Не прочитано» / «Завершенные»
// и шестерёнка, которая включает режим выбора: чекбоксы у строк и полоса
// действий «Снять выбор / Закрепить / Удалить». Закреплённые треды — сверху
// (порядок отдаёт шлюз). ?conversation=<id> открывает тред, ?product=<id> —
// тред по товару (с карточки «В чат с продавцом»/«Написать продавцу»): уже
// заведённый или черновик, который становится тредом с первым сообщением.
// Товар с карточки не улетает сам: он прикреплён к полю ввода (вложение над
// полем, крестик убирает), и уходит вместе с сообщением по «Отправить»
// (Айталы A-07). Без текста можно отправить один товар — шлюз это принимает.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, ShieldAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type ChatConversation, type ChatMessage, type ChatOffer, type Product } from "@/api";
import { chatKeys, useBuyerConversation, useBuyerConversations } from "../api/use-chats";
import { ChatIcon, ChevronLeftIcon, ExternalLinkIcon, InfoCircle20Icon, PinIcon, PlusIcon, SendIcon, SettingsIcon, XIcon } from "@/components/icons";
import { SignInCard } from "@/components/profile/sign-in-card";
import { VerifiedSellerBadge } from "@/components/shop/verified-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip } from "@/components/ui/chip";
import { Input } from "@/components/ui/text-field";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { useCart, useProfile, useWishlist } from "@/store";
type Filter = "all" | "unread" | "completed";
const ACCENT = "#FA5C2E";
export function ChatsPage() {
  const $ = _c(9);
  const token = useProfile(_temp);
  const user = useProfile(_temp2);
  const hydrateMe = useProfile(_temp3);
  const syncWishlist = useWishlist(_temp4);
  let t0;
  let t1;
  if ($[0] !== hydrateMe || $[1] !== token || $[2] !== user) {
    t0 = () => {
      if (token && !user) {
        hydrateMe().catch(_temp5);
      }
    };
    t1 = [hydrateMe, token, user];
    $[0] = hydrateMe;
    $[1] = token;
    $[2] = user;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  if (!user) {
    let t2;
    if ($[5] !== syncWishlist) {
      t2 = <SignInCard onSignedIn={() => void syncWishlist()} />;
      $[5] = syncWishlist;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  let t2;
  if ($[7] !== user.id) {
    t2 = <ChatsWorkspace userId={user.id} />;
    $[7] = user.id;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}
function _temp5() {}
function _temp4(s_2) {
  return s_2.syncFromServer;
}
function _temp3(s_1) {
  return s_1.hydrateMe;
}
function _temp2(s_0) {
  return s_0.user;
}
function _temp(s) {
  return s.token;
}
function ChatsWorkspace({
  userId
}: {
  userId: string;
}) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const list = useBuyerConversations();
  const conversations = list.data ?? [];
  const [filter, setFilter] = useState<Filter>("all");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const requestedId = params.get("conversation");
  // Пришли с карточки товара: тред по товару, если он уже есть, иначе черновик.
  const productParam = params.get("product");
  const productThread = productParam ? conversations.find(c => c.productId === productParam && !c.manufacturingRequestId) : undefined;
  const draftProductId = productParam && !list.isPending && !productThread ? productParam : null;
  // «Написать продавцу» из шапки витрины (fd-boutique-01): ?shop=<slug> —
  // тред с магазином без товара, уже заведённый или черновик.
  const shopParam = params.get("shop");
  const shopThread = shopParam ? conversations.find(c_0 => (c_0.shopSlug === shopParam || c_0.shopId === shopParam) && c_0.context.kind === "shop") : undefined;
  const draftShopSlug = shopParam && !list.isPending && !shopThread ? shopParam : null;
  useEffect(() => {
    if (shopThread && requestedId !== shopThread.id) setParams({
      conversation: shopThread.id
    }, {
      replace: true
    });
  }, [shopThread, requestedId, setParams]);
  // Тред по товару уже есть — открываем его, а товар остаётся в ссылке: он
  // прикреплён к полю ввода и уйдёт с ближайшим сообщением (бриф, как в
  // приложении). Убрали вложение или отправили — `product` из ссылки уходит.
  // `consumed` — товар уже ушёл с сообщением (или его убрали): ссылка могла
  // ещё не обновиться, а тред уже в списке, — повторно не прикрепляем.
  const [consumed, setConsumed] = useState<string | null>(null);
  const attachable = productParam && productParam !== consumed ? productParam : null;
  useEffect(() => {
    if (productThread && attachable && requestedId !== productThread.id) setParams({
      conversation: productThread.id,
      product: attachable
    }, {
      replace: true
    });
  }, [productThread, attachable, requestedId, setParams]);
  const unreadTotal = conversations.filter(c_1 => c_1.unreadCount > 0).reduce((s, c_2) => s + c_2.unreadCount, 0);
  const visible = conversations.filter(c_3 => filter === "unread" ? c_3.unreadCount > 0 : filter === "completed" ? Boolean(c_3.isCompleted) : true);
  // Тред из ссылки держим открытым, даже если он выпал из текущего фильтра.
  const activeId = (requestedId && conversations.some(c_4 => c_4.id === requestedId) ? requestedId : null) ?? (!productParam && !shopParam && typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches ? visible[0]?.id ?? null : null);
  const active = conversations.find(c_5 => c_5.id === activeId) ?? null;
  const listKey = chatKeys.list(userId);
  const patchList = (fn: (items: ChatConversation[]) => ChatConversation[]) => qc.setQueryData<ChatConversation[]>(listKey, prev => prev ? fn(prev) : prev);
  const open = (id: string) => {
    setParams({
      conversation: id
    }, {
      replace: true
    });
    // Сразу гасим бейдж — шлюз отметит прочитанным и при загрузке треда.
    patchList(items => items.map(c_6 => c_6.id === id ? {
      ...c_6,
      unreadCount: 0
    } : c_6));
    void api.buyer.markConversationRead(id).catch(() => undefined);
  };
  const toggle = (id_0: string) => setSelected(prev_0 => {
    const next = new Set(prev_0);
    if (next.has(id_0)) next.delete(id_0);else next.add(id_0);
    return next;
  });
  const exitSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };
  const chosen = conversations.filter(c_7 => selected.has(c_7.id));
  const allPinned = chosen.length > 0 && chosen.every(c_8 => c_8.isPinned);
  const run = async (fn_0: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn_0();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Не удалось выполнить действие");
    } finally {
      setBusy(false);
      void qc.invalidateQueries({
        queryKey: listKey
      });
    }
  };
  const pin = () => run(async () => {
    const ids = [...selected];
    const pinned = !allPinned;
    await api.buyer.pinConversations(ids, pinned);
    patchList(items_0 => items_0.map(c_9 => selected.has(c_9.id) ? {
      ...c_9,
      isPinned: pinned
    } : c_9));
    toast.success(pinned ? "Чаты закреплены" : "Чаты откреплены");
    exitSelecting();
  });

  /**
   * Открыть чат с поддержкой площадки.
   *
   * Ручка гейтвея была, вход в вебе — нет: покупатель писал в поддержку из
   * раздела помощи, а отвечали ему в чатах. Теперь это один и тот же тред,
   * и открывается он оттуда же, где все остальные разговоры.
   */
  const openSupport = () => run(async () => {
    const conversation = await api.buyer.openSupportConversation();
    await qc.invalidateQueries({
      queryKey: listKey
    });
    open(conversation.id);
  });
  const remove = () => run(async () => {
    const ids_0 = [...selected];
    await api.buyer.deleteConversations(ids_0);
    patchList(items_1 => items_1.filter(c_10 => !selected.has(c_10.id)));
    if (activeId && selected.has(activeId)) setParams({}, {
      replace: true
    });
    toast.success(ids_0.length === 1 ? "Чат удалён" : `Удалено чатов: ${ids_0.length}`);
    exitSelecting();
  });

  // Первое сообщение черновика завело тред: кладём его в список сразу (иначе до
  // ответа списка открылся бы чужой чат) и переключаемся на него.
  const started = (conversation_0: ChatConversation) => {
    setConsumed(productParam);
    qc.setQueryData<ChatConversation[]>(listKey, prev_1 => [conversation_0, ...(prev_1 ?? []).filter(c_11 => c_11.id !== conversation_0.id)]);
    void qc.invalidateQueries({
      queryKey: listKey
    });
    setParams({
      conversation: conversation_0.id
    }, {
      replace: true
    });
  };
  const allVisibleSelected = visible.length > 0 && visible.every(c_12 => selected.has(c_12.id));
  const paneOpen = Boolean(active || productParam || shopParam);
  return (
    // Серый фон #F8F8F8 — во всю ширину (тень-заливка 100vmax), но строго от
    // нижнего края шапки: обёртка поднята на верхний отступ <main> (10) и
    // обрезана по своему верху. Раньше заливка выезжала на 20px под шапку
    // (Айталы A-04). Низ — до конца <main>, подвал остаётся на белом.
    <div className="mx-auto -mt-2.5 w-full max-w-[1140px] bg-[#F8F8F8] pt-2.5 shadow-[0_0_0_100vmax_#F8F8F8] [clip-path:inset(0_-100vmax_-24px)] md:[clip-path:inset(0_-100vmax_-32px)]">
			{/* Кадр 3927:46763: заголовок в 10px под шапкой, Manrope 20/23 w600 ls −0,31 #2E2E2E. */}
			<h1 className="text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#2E2E2E]">Чаты</h1>

			{selecting ?
      // Кадры «Управление» 3927:48612 / 49056: белая полоса 48, r12, pad 8, gap 12.
      <div className="mt-5 flex h-12 items-center gap-3 overflow-x-auto rounded-[12px] bg-white px-2 [scrollbar-width:none]">
					{/* Чекбокс DS 20 r5; «Выбрано N» 14/18 w400 (gap 10). */}
					<Checkbox checked={allVisibleSelected} aria-label="Выбрать все" onCheckedChange={() => setSelected(allVisibleSelected ? new Set() : new Set(visible.map(c_13 => c_13.id)))} />
					<span className="-ml-0.5 shrink-0 text-[14px] leading-[18px] text-ds-primary">Выбрано {selected.size}</span>
					{/* Кнопки DS S (32, r8, 12/16 w600): серые, «Удалить» — danger; без
         выбора все серые с подписью #8A8A8B 46 % (кадр 3927:48612). */}
					<Button variant="gray" size="sm" disabled={selected.size === 0 || busy} onClick={() => setSelected(new Set())}>
						Снять выбор
					</Button>
					<Button variant="gray" size="sm" disabled={selected.size === 0 || busy} onClick={() => void pin()}>
						<PinIcon size={16} />
						{allPinned ? "Открепить" : "Закрепить"}
					</Button>
					<Button variant="danger" size="sm" disabled={selected.size === 0 || busy} onClick={() => void remove()}>
						Удалить
					</Button>
					<button type="button" onClick={exitSelecting} aria-label="Выйти из режима выбора" className="ml-auto grid size-8 shrink-0 place-items-center rounded-[8px] text-ds-primary outline-none hover:bg-ds-quaternary focus-visible:ring-2 focus-visible:ring-ds-focus">
						<XIcon size={24} />
					</button>
				</div> :
      // На телефоне чипы не помещаются в строку — она листается, а не
      // растягивает страницу вбок.
      <div className="mt-5 flex h-8 items-center gap-2.5 overflow-x-auto [scrollbar-width:none]">
					{/* Чипы DS sm (h32 pad 0/12, 14/18 w500), счётчик непрочитанных 16 #E1574F. */}
					<Chip selected={filter === "unread"} count={Math.min(unreadTotal, 99)} countTone="alert" onClick={() => setFilter(f => f === "unread" ? "all" : "unread")}>
						Не прочитано
					</Chip>
					<Chip selected={filter === "completed"} onClick={() => setFilter(f_0 => f_0 === "completed" ? "all" : "completed")}>
						Завершенные
					</Chip>
					{/* Единый вход в поддержку: у покупателя один список разговоров, а не
         «чаты с магазинами здесь, а помощь где-то в другом разделе». Тред
         один на аккаунт — повторное нажатие открывает его же. */}
					<button type="button" disabled={busy} onClick={() => void openSupport()} className="flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-ds-primary/[0.12] px-3 text-[14px] leading-[18px] font-medium text-ds-primary outline-none transition-colors hover:border-ds-accent focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-40">
						<LifeBuoy className="size-4" strokeWidth={1.75} />
						Поддержка OSMIX
					</button>
					<button type="button" onClick={() => setSelecting(true)} disabled={conversations.length === 0} aria-label="Выбрать чаты" className="-mr-1 ml-auto grid size-8 shrink-0 place-items-center rounded-full text-ds-primary outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ds-focus disabled:opacity-40">
						<SettingsIcon size={24} />
					</button>
				</div>}

			<div className="mt-5 grid gap-5 lg:h-[557px] lg:grid-cols-[367px_minmax(0,1fr)]">
				<aside className={cn("min-h-0 overflow-hidden rounded-[20px] border border-[#F8F8F8] bg-white", paneOpen && "hidden lg:block")}>
					{list.isPending ? <ListSkeleton /> : visible.length === 0 ? <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 px-8 text-center">
							<ChatIcon size={32} className="text-ds-muted" />
							<p className="text-[14px] font-medium text-[#1F1F1F]">
								{filter === "all" ? "Здесь появятся переписки с магазинами" : "Нет чатов по этому фильтру"}
							</p>
							{filter === "all" && <p className="text-[13px] text-black/50">
									Напишите магазину со страницы товара — ответ придёт сюда.
								</p>}
						</div> : <ul className="h-full overflow-y-auto">
							{visible.map(c_14 => <li key={c_14.id} className="border-b border-[#F8F8F8] last:border-b-0">
									<ConversationRow conversation={c_14} active={c_14.id === activeId} selecting={selecting} checked={selected.has(c_14.id)} onToggle={() => toggle(c_14.id)} onOpen={() => open(c_14.id)} />
								</li>)}
						</ul>}
				</aside>

				<section className={cn("flex min-h-[520px] min-w-0 flex-col overflow-hidden rounded-[20px] border border-[#F8F8F8] bg-[#F8F8F8] shadow-[0_10px_20px_rgba(0,0,0,0.05)] lg:min-h-0", !paneOpen && "hidden lg:flex")}>
					{active ? <Thread key={active.id} userId={userId} summary={active} attachProductId={productThread?.id === active.id ? attachable : null} onDetach={() => {
            setConsumed(productParam);
            setParams({
              conversation: active.id
            }, {
              replace: true
            });
          }} onBack={() => setParams({}, {
            replace: true
          })} /> : draftProductId ? <DraftThread key={draftProductId} productId={draftProductId} onStarted={started} onBack={() => setParams({}, {
            replace: true
          })} /> : draftShopSlug ? <DraftShopThread key={draftShopSlug} shopSlug={draftShopSlug} onStarted={started} onBack={() => setParams({}, {
            replace: true
          })} /> : productParam || shopParam ? <div className="flex flex-1 items-center justify-center bg-white">
							<output aria-label="Загрузка" className="size-6 animate-spin rounded-full border-2 border-[#ABABAB] border-t-transparent" />
						</div> : <div className="flex flex-1 flex-col items-center justify-center gap-2 bg-white text-center">
							<ChatIcon size={32} className="text-ds-muted" />
							<p className="text-[14px] text-black/50">Выберите чат слева</p>
						</div>}
				</section>
			</div>
		</div>
  );
}

// ── Список ──────────────────────────────────────────────────────────────────

const timeLabel = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay ? d.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit"
  }) : d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit"
  });
};
function ShopAvatar(t0) {
  const $ = _c(13);
  const {
    name,
    logo,
    size: t1
  } = t0;
  const size = t1 === undefined ? 40 : t1;
  const [broken, setBroken] = useState(false);
  if (logo && !broken) {
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = () => setBroken(true);
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    let t3;
    if ($[1] !== size) {
      t3 = {
        width: size,
        height: size
      };
      $[1] = size;
      $[2] = t3;
    } else {
      t3 = $[2];
    }
    let t4;
    if ($[3] !== logo || $[4] !== t3) {
      t4 = <img src={logo} alt="" onError={t2} className="shrink-0 rounded-full object-cover" style={t3} />;
      $[3] = logo;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  let t2;
  if ($[6] !== size) {
    t2 = {
      width: size,
      height: size
    };
    $[6] = size;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== name) {
    t3 = name.slice(0, 1).toUpperCase();
    $[8] = name;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <span className="grid shrink-0 place-items-center rounded-full bg-[#FA5C2E] text-[16px] font-semibold text-white" style={t2} aria-hidden="true">{t3}</span>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function ConversationRow(t0) {
  const $ = _c(58);
  const {
    conversation: c,
    active,
    selecting,
    checked,
    onToggle,
    onOpen
  } = t0;
  const unread = c.unreadCount > 0;
  let t1;
  if ($[0] !== c.lastMessagePreview || $[1] !== c.subject) {
    t1 = c.lastMessagePreview?.trim() || (c.lastMessagePreview === "" ? "\u0424\u043E\u0442\u043E" : c.subject);
    $[0] = c.lastMessagePreview;
    $[1] = c.subject;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const preview = t1;
  const t2 = selecting ? onToggle : onOpen;
  let t3;
  if ($[3] !== onOpen || $[4] !== onToggle || $[5] !== selecting) {
    t3 = e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (selecting) {
          onToggle();
        } else {
          onOpen();
        }
      }
    };
    $[3] = onOpen;
    $[4] = onToggle;
    $[5] = selecting;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = active ? "bg-[#F8F8F8]" : "hover:bg-[#F8F8F8]/60";
  let t5;
  if ($[7] !== t4) {
    t5 = cn("relative flex h-[74px] cursor-pointer items-center gap-[10px] px-3 outline-none transition-colors focus-visible:bg-[#F8F8F8]", t4);
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== active) {
    t6 = active && <span className="absolute inset-y-0 left-0 w-1 bg-[#FA5C2E]" aria-hidden="true" />;
    $[9] = active;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== checked || $[12] !== onToggle || $[13] !== selecting) {
    t7 = selecting && <Checkbox checked={checked} aria-label={"\u0412\u044B\u0431\u0440\u0430\u0442\u044C \u0447\u0430\u0442"} className="size-6 rounded-[6px]" onClick={_temp6} onCheckedChange={onToggle} />;
    $[11] = checked;
    $[12] = onToggle;
    $[13] = selecting;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== c.shopId || $[16] !== c.shopName) {
    t8 = c.shopName?.trim() || c.shopId;
    $[15] = c.shopId;
    $[16] = c.shopName;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== c.shopLogo || $[19] !== t8) {
    t9 = <ShopAvatar name={t8} logo={c.shopLogo} />;
    $[18] = c.shopLogo;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== c.shopId || $[22] !== c.shopName) {
    t10 = c.shopName?.trim() || c.shopId;
    $[21] = c.shopId;
    $[22] = c.shopName;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10) {
    t11 = <span className="min-w-0 flex-1 truncate text-[14px] leading-[18px] font-bold text-[#1F1F1F]">{t10}</span>;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== c.isPinned) {
    t12 = c.isPinned && <PinIcon size={12} className="shrink-0 text-black/40" aria-label={"\u0417\u0430\u043A\u0440\u0435\u043F\u043B\u0451\u043D"} />;
    $[26] = c.isPinned;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== c.lastMessageAt) {
    t13 = timeLabel(c.lastMessageAt);
    $[28] = c.lastMessageAt;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <span className="shrink-0 text-[12px] leading-4 text-black/50">{t13}</span>;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t14) {
    t15 = <div className="flex items-center gap-1">{t11}{t12}{t14}</div>;
    $[32] = t11;
    $[33] = t12;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const t16 = unread && "leading-4 font-extrabold";
  let t17;
  if ($[36] !== t16) {
    t17 = cn("min-w-0 flex-1 truncate text-[12px] leading-5 text-[#1F1F1F]", t16);
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== preview || $[39] !== t17) {
    t18 = <span className={t17}>{preview}</span>;
    $[38] = preview;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== c.unreadCount || $[42] !== unread) {
    t19 = unread && <span className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full bg-[#E1574F] px-0.5 text-[12px] leading-4 font-medium text-white">{c.unreadCount > 99 ? "99+" : c.unreadCount}</span>;
    $[41] = c.unreadCount;
    $[42] = unread;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t18 || $[45] !== t19) {
    t20 = <div className="flex h-5 items-center gap-4">{t18}{t19}</div>;
    $[44] = t18;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t15 || $[48] !== t20) {
    t21 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t15}{t20}</div>;
    $[47] = t15;
    $[48] = t20;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== t2 || $[51] !== t21 || $[52] !== t3 || $[53] !== t5 || $[54] !== t6 || $[55] !== t7 || $[56] !== t9) {
    t22 = <div role="button" tabIndex={0} onClick={t2} onKeyDown={t3} data-testid="chat-row" className={t5}>{t6}{t7}{t9}{t21}</div>;
    $[50] = t2;
    $[51] = t21;
    $[52] = t3;
    $[53] = t5;
    $[54] = t6;
    $[55] = t7;
    $[56] = t9;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
}
function _temp6(e_0) {
  return e_0.stopPropagation();
}
function ListSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col">{Array.from({
        length: 6
      }, _temp7)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

// ── Тред ────────────────────────────────────────────────────────────────────
function _temp7(_, i) {
  return <div key={i} className="flex h-[74px] items-center gap-3 border-b border-[#F8F8F8] px-[13px]"><span className="size-10 animate-pulse rounded-full bg-[#F2F2F2]" /><span className="flex flex-1 flex-col gap-2"><span className="h-3 w-1/2 animate-pulse rounded-[4px] bg-[#F2F2F2]" /><span className="h-3 w-3/4 animate-pulse rounded-[4px] bg-[#F2F2F2]" /></span></div>;
}
type TimelineItem = {
  kind: "message";
  at: string;
  message: ChatMessage;
} | {
  kind: "offer";
  at: string;
  offer: ChatOffer;
};

// Шлюз при создании оффера пишет в ленту служебное «Предложение: …» — в вебе
// его заменяет карточка оффера, поэтому такой текст дублем не показываем.
const isOfferEcho = (m: ChatMessage, offers: ChatOffer[]) => m.authorRole === "seller" && m.body.startsWith("Предложение: ") && offers.some(o => m.body === `Предложение: ${o.description}`);

// Разделитель дня в ленте (кадр «Чаты»: «Сегодня», «20.08», «20.08.2024»).
const dayKey = (iso: string) => new Date(iso).toDateString();
const dayLabel = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return "Сегодня";
  return d.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    ...(d.getFullYear() === now.getFullYear() ? {} : {
      year: "numeric"
    })
  });
};
function DaySeparator(t0) {
  const $ = _c(7);
  const {
    iso,
    first
  } = t0;
  const t1 = !first && "mt-2.5";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("text-center text-[14px] leading-[18px] text-black/50", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== iso) {
    t3 = dayLabel(iso);
    $[2] = iso;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <p className={t2}>{t3}</p>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}

// Шапка треда (кадр «Чаты», navbar 60): аватар 40, gap 8, имя 16/20 w600
// ls −0,31 #161616, справа «В магазин» 14/18.
function ThreadHeader(t0) {
  const $ = _c(25);
  const {
    shopName,
    shopSlug,
    shopLogo,
    onBack,
    loading
  } = t0;
  const verified = useShopVerified(shopSlug);
  let t1;
  if ($[0] !== shopLogo || $[1] !== shopName) {
    t1 = <ShopAvatar name={shopName} logo={shopLogo} />;
    $[0] = shopLogo;
    $[1] = shopName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== shopName) {
    t2 = <span className="min-w-0 truncate text-[16px] leading-5 font-semibold tracking-[-0.31px] text-[#161616] group-hover:text-[#FA5C2E]">{shopName}</span>;
    $[3] = shopName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== verified) {
    t3 = verified && <VerifiedSellerBadge compact={true} />;
    $[5] = verified;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const name = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ChevronLeftIcon size={24} />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== onBack) {
    t6 = <button type="button" onClick={onBack} aria-label={"\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u0447\u0430\u0442\u043E\u0432"} className="-ml-1 grid size-8 place-items-center rounded-full text-[#333] lg:hidden">{t5}</button>;
    $[12] = onBack;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== loading || $[15] !== name || $[16] !== shopSlug) {
    t7 = loading ? <span className="h-4 w-40 animate-pulse rounded-[4px] bg-[#F2F2F2]" /> : shopSlug ? <Link to={`/shop/${shopSlug}`} className="group flex min-w-0 flex-1 items-center gap-2 rounded-[8px] outline-none focus-visible:ring-2 focus-visible:ring-[#FA5C2E]/40">{name}</Link> : <span className="flex min-w-0 flex-1 items-center gap-2">{name}</span>;
    $[14] = loading;
    $[15] = name;
    $[16] = shopSlug;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== loading || $[19] !== shopSlug) {
    t8 = !loading && shopSlug && <Link to={`/shop/${shopSlug}`} className="shrink-0 rounded-[4px] text-[14px] leading-[18px] text-[#1F1F1F] outline-none hover:text-[#FA5C2E] focus-visible:ring-2 focus-visible:ring-[#FA5C2E]/40">В магазин</Link>;
    $[18] = loading;
    $[19] = shopSlug;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t6 || $[22] !== t7 || $[23] !== t8) {
    t9 = <header className="flex h-[60px] shrink-0 items-center gap-3 border-b border-[#F8F8F8] bg-white px-4">{t6}{t7}{t8}</header>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}

// «Проверенный продавец» в шапке переписки (fd-onboard-12): флаг из общего
// списка магазинов - того же кеша, что у карточки товара.
function useShopVerified(slug) {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["shops", "list"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = Boolean(slug);
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: _temp8,
      enabled: t1
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const shops = useQuery(t2);
  let t3;
  if ($[3] !== shops || $[4] !== slug) {
    t3 = slug && shops.data?.find(s => s.slug === slug || s.id === slug)?.verified;
    $[3] = shops;
    $[4] = slug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return Boolean(t3);
}

// Товар, прикреплённый к полю ввода: тот, с карточки которого пришли.
function _temp8() {
  return api.shops.list();
}
function useAttachedProduct(productId) {
  const $ = _c(8);
  const t0 = productId ?? "none";
  let t1;
  if ($[0] !== t0) {
    t1 = ["products", "get", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== productId) {
    t2 = () => api.products.get(productId ?? "");
    $[2] = productId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = Boolean(productId);
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}
function Thread(t0) {
  const $ = _c(43);
  const {
    userId,
    summary,
    attachProductId,
    onDetach,
    onBack
  } = t0;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const acceptOffer = useCart(_temp9);
  const detail = useBuyerConversation(summary.id);
  const conversation = detail.data ?? summary;
  const messages = conversation.messages ?? [];
  const offers = conversation.offers ?? [];
  const scroller = useRef(null);
  const attached = useAttachedProduct(attachProductId);
  const timeline = [...messages.filter(m => !isOfferEcho(m, offers)).map(_temp0), ...offers.map(_temp1)].sort(_temp10);
  let t1;
  let t2;
  if ($[0] !== detail.data || $[1] !== qc || $[2] !== summary.id || $[3] !== userId) {
    t1 = () => {
      if (!detail.data) {
        return;
      }
      qc.setQueryData(chatKeys.list(userId), prev => prev?.some(c_0 => c_0.id === summary.id && c_0.unreadCount > 0) ? prev.map(c => c.id === summary.id ? {
        ...c,
        unreadCount: 0
      } : c) : prev);
    };
    t2 = [detail.data, qc, userId, summary.id];
    $[0] = detail.data;
    $[1] = qc;
    $[2] = summary.id;
    $[3] = userId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const lastKey = timeline.at(-1)?.at;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      scroller.current?.scrollTo({
        top: scroller.current.scrollHeight
      });
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t3, [lastKey]);
  let t4;
  if ($[7] !== qc || $[8] !== summary.id || $[9] !== userId) {
    t4 = async () => {
      await Promise.all([qc.invalidateQueries({
        queryKey: chatKeys.detail(userId, summary.id)
      }), qc.invalidateQueries({
        queryKey: chatKeys.list(userId)
      })]);
    };
    $[7] = qc;
    $[8] = summary.id;
    $[9] = userId;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const refresh = t4;
  let t5;
  if ($[11] !== acceptOffer || $[12] !== navigate || $[13] !== refresh) {
    t5 = async offer => {
      if (!offer.productId) {
        ;
        try {
          await api.buyer.acceptOfferInChat(offer.id);
          toast.success("\u041E\u0442\u043A\u043B\u0438\u043A \u043F\u0440\u0438\u043D\u044F\u0442 - \u043F\u0440\u043E\u0434\u0430\u0432\u0435\u0446 \u043F\u0440\u0438\u0448\u043B\u0451\u0442 \u0441\u0447\u0451\u0442 \u0432 \u0447\u0430\u0442\u0435");
          await refresh();
        } catch (t6) {
          const e = t6;
          toast.error(e instanceof Error ? e.message : "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043F\u0440\u0438\u043D\u044F\u0442\u044C \u043E\u0442\u043A\u043B\u0438\u043A");
        }
        return;
      }
      ;
      try {
        await acceptOffer(offer.id);
        toast.success("\u041F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u0435 \u0434\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u043E \u0432 \u043A\u043E\u0440\u0437\u0438\u043D\u0443");
        navigate("/cart");
      } catch (t7) {
        const e_0 = t7;
        toast.error(e_0 instanceof Error ? e_0.message : "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0434\u043E\u0431\u0430\u0432\u0438\u0442\u044C \u0432 \u043A\u043E\u0440\u0437\u0438\u043D\u0443");
      }
    };
    $[11] = acceptOffer;
    $[12] = navigate;
    $[13] = refresh;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const pay = t5;
  let t6;
  if ($[15] !== conversation.shopId || $[16] !== conversation.shopName) {
    t6 = conversation.shopName?.trim() || conversation.shopId;
    $[15] = conversation.shopId;
    $[16] = conversation.shopName;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const shopName = t6;
  const attachment = attachProductId && attached.data ? attached.data : null;
  const offPlatformRisk = messages.some(_temp11);
  let t7;
  if ($[18] !== conversation.shopLogo || $[19] !== conversation.shopSlug || $[20] !== onBack || $[21] !== shopName) {
    t7 = <ThreadHeader shopName={shopName} shopSlug={conversation.shopSlug} shopLogo={conversation.shopLogo} onBack={onBack} />;
    $[18] = conversation.shopLogo;
    $[19] = conversation.shopSlug;
    $[20] = onBack;
    $[21] = shopName;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const t8 = offPlatformRisk && <OffPlatformBanner />;
  const t9 = "flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-2.5";
  const t10 = "buyer-chat-thread";
  const t11 = conversation.id;
  const t12 = timeline.length === 0 && !detail.isPending && <p className="m-auto text-[13px] text-black/50">Сообщений пока нет — напишите магазину.</p>;
  const t13 = timeline.map((item, i) => {
    const prev_0 = timeline[i - 1];
    const fromSeller = item.kind === "offer" || item.message.authorRole === "seller";
    const newDay = !prev_0 || dayKey(prev_0.at) !== dayKey(item.at);
    const prevFromSeller = prev_0 && !newDay ? prev_0.kind === "offer" || prev_0.message.authorRole === "seller" : false;
    const showAuthor = fromSeller && !prevFromSeller;
    return <div key={item.kind === "offer" ? item.offer.id : item.message.id} className="flex flex-col gap-2.5">{newDay && <DaySeparator iso={item.at} first={i === 0} />}<div className="flex flex-col gap-1.5">{showAuthor && <p className="text-[14px] leading-[18px] font-semibold text-[#1F1F1F]">{shopName}</p>}{item.kind === "offer" ? <OfferBubble offer={item.offer} onPay={() => void pay(item.offer)} /> : <MessageBubble message={item.message} />}</div></div>;
  });
  let t14;
  if ($[23] !== conversation.id || $[24] !== t12 || $[25] !== t13) {
    t14 = <div ref={scroller} className={t9} data-testid={t10} data-conversation-id={t11}>{t12}{t13}</div>;
    $[23] = conversation.id;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== attachment || $[28] !== onDetach) {
    t15 = attachment ? {
      product: attachment,
      onRemove: onDetach
    } : undefined;
    $[27] = attachment;
    $[28] = onDetach;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== attachment || $[31] !== conversation.id || $[32] !== onDetach || $[33] !== refresh) {
    t16 = async (body, imageUris) => {
      await api.buyer.replyConversation(conversation.id, body, imageUris, attachment ? {
        productId: attachment.id
      } : undefined);
      if (attachment) {
        onDetach();
      }
      await refresh();
    };
    $[30] = attachment;
    $[31] = conversation.id;
    $[32] = onDetach;
    $[33] = refresh;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <Composer attachment={t15} onSend={t16} />;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t14 || $[39] !== t17 || $[40] !== t7 || $[41] !== t8) {
    t18 = <>{t7}{t8}{t14}{t17}</>;
    $[38] = t14;
    $[39] = t17;
    $[40] = t7;
    $[41] = t8;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
}

/*
 * Черновик треда по товару (`?product=`): переписки ещё нет, шлюз заводит тред
 * только с первым сообщением. Шапка — магазин товара, лента пустая, а товар
 * прикреплён к полю ввода и уйдёт с первым сообщением (Айталы A-07: запрос сам
 * не улетает). Отправить можно и один товар без текста — шлюз это принимает;
 * фото, если есть, уходят следом в тот же тред. Убрать товар из черновика —
 * значит не писать о нём: без товара треда с магазином нет, возвращаемся к
 * списку чатов.
 */
function _temp11(m_1) {
  return m_1.masked;
}
function _temp10(a, b) {
  return a.at.localeCompare(b.at);
}
function _temp1(o) {
  return {
    kind: "offer" as const,
    at: o.createdAt,
    offer: o
  };
}
function _temp0(m_0) {
  return {
    kind: "message" as const,
    at: m_0.createdAt,
    message: m_0
  };
}
function _temp9(s) {
  return s.acceptOffer;
}
function DraftThread(t0) {
  const $ = _c(27);
  const {
    productId,
    onStarted,
    onBack
  } = t0;
  const product = useAttachedProduct(productId);
  const p = product.data;
  let t1;
  if ($[0] !== p?.brand || $[1] !== p?.sellerInfo?.name) {
    t1 = p?.sellerInfo?.name?.trim() || p?.brand || "";
    $[0] = p?.brand;
    $[1] = p?.sellerInfo?.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shopName = t1;
  const t2 = p?.sellerInfo?.slug;
  const t3 = !p;
  let t4;
  if ($[3] !== onBack || $[4] !== shopName || $[5] !== t2 || $[6] !== t3) {
    t4 = <ThreadHeader shopName={shopName} shopSlug={t2} onBack={onBack} loading={t3} />;
    $[3] = onBack;
    $[4] = shopName;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== p || $[9] !== product.isError) {
    t5 = product.isError ? <p className="m-auto text-[13px] text-black/50">Товар не найден — вернитесь к списку чатов.</p> : p ? <p className="m-auto max-w-[320px] text-center text-[13px] leading-[18px] text-black/50">Товар прикреплён к сообщению. Напишите продавцу и нажмите «Отправить» — ответ придёт сюда.</p> : null;
    $[8] = p;
    $[9] = product.isError;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-2.5" data-testid="buyer-chat-draft">{t5}</div>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = !p;
  let t8;
  if ($[13] !== onBack || $[14] !== p) {
    t8 = p ? {
      product: p,
      onRemove: onBack
    } : undefined;
    $[13] = onBack;
    $[14] = p;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== onStarted || $[17] !== productId) {
    t9 = async (body, imageUris) => {
      const conversation = await api.buyer.startProductConversation({
        productId,
        message: body
      });
      if (imageUris.length) {
        await api.buyer.replyConversation(conversation.id, "", imageUris);
      }
      onStarted(conversation);
    };
    $[16] = onStarted;
    $[17] = productId;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <Composer disabled={t7} attachment={t8} onSend={t9} />;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t4 || $[25] !== t6) {
    t11 = <>{t4}{t6}{t10}</>;
    $[23] = t10;
    $[24] = t4;
    $[25] = t6;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
}

/*
 * Черновик треда с магазином (`?shop=<slug>`, «Написать продавцу» с витрины,
 * fd-boutique-01): переписки ещё нет, шлюз заводит тред первым сообщением.
 */
function DraftShopThread(t0) {
  const $ = _c(23);
  const {
    shopSlug,
    onStarted,
    onBack
  } = t0;
  let t1;
  if ($[0] !== shopSlug) {
    t1 = {
      queryKey: ["shops", "get", shopSlug],
      queryFn: () => api.shops.get(shopSlug),
      retry: false
    };
    $[0] = shopSlug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const shop = useQuery(t1);
  const s = shop.data;
  const t2 = s?.name ?? "";
  const t3 = s?.slug;
  const t4 = s?.logoUrl;
  const t5 = !s;
  let t6;
  if ($[2] !== onBack || $[3] !== t2 || $[4] !== t3 || $[5] !== t4 || $[6] !== t5) {
    t6 = <ThreadHeader shopName={t2} shopSlug={t3} shopLogo={t4} onBack={onBack} loading={t5} />;
    $[2] = onBack;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== s || $[9] !== shop.isError) {
    t7 = shop.isError ? <p className="m-auto text-[13px] text-black/50">Магазин не найден — вернитесь к списку чатов.</p> : s ? <p className="m-auto max-w-[320px] text-center text-[13px] leading-[18px] text-black/50">Напишите магазину и нажмите «Отправить» — ответ придёт сюда.</p> : null;
    $[8] = s;
    $[9] = shop.isError;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-2.5" data-testid="buyer-chat-shop-draft">{t7}</div>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const t9 = !s;
  let t10;
  if ($[13] !== onStarted || $[14] !== shopSlug) {
    t10 = async (body, imageUris) => {
      const conversation = await api.buyer.startShopConversation({
        shopSlug,
        message: body,
        imageUris
      });
      onStarted(conversation);
    };
    $[13] = onStarted;
    $[14] = shopSlug;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <Composer disabled={t9} onSend={t10} />;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t6 || $[21] !== t8) {
    t12 = <>{t6}{t8}{t11}</>;
    $[19] = t11;
    $[20] = t6;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  return t12;
}
const clock = (iso: string) => new Date(iso).toLocaleTimeString("ru-RU", {
  hour: "2-digit",
  minute: "2-digit"
});

// Карточка товара в сообщении (кадр «Чаты», «Сообщение от покупателя»):
// подложка #F8F8F8 r20 pad 8 gap 12, фото 100×133 r16, цена 14/14 w700 #333,
// название 14/15 w600 #161616, справа — переход к товару.
function ProductAttachmentCard(t0) {
  const $ = _c(16);
  const {
    productId,
    name,
    photo,
    price
  } = t0;
  const t1 = `/product/${encodeURIComponent(productId)}`;
  let t2;
  if ($[0] !== photo) {
    t2 = photo ? <img src={photo} alt="" className="size-full object-cover" /> : null;
    $[0] = photo;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="h-[133px] w-[100px] shrink-0 overflow-hidden rounded-[16px] bg-white">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== price) {
    t4 = price && <span className="text-[14px] leading-[14px] font-bold tracking-[-0.42px] text-[#333]">{price}</span>;
    $[4] = price;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== name) {
    t5 = <span className="line-clamp-3 text-[14px] leading-[15px] font-semibold tracking-[-0.28px] text-[#161616]">{name}</span>;
    $[6] = name;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = <span className="flex min-w-0 flex-1 flex-col gap-2">{t4}{t5}</span>;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <ExternalLinkIcon size={20} className="shrink-0 text-ds-primary" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t3 || $[14] !== t6) {
    t8 = <Link to={t1} className="flex w-full gap-3 rounded-[20px] bg-[#F8F8F8] p-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-[#FA5C2E]/40" data-testid="chat-product-attachment">{t3}{t6}{t7}</Link>;
    $[12] = t1;
    $[13] = t3;
    $[14] = t6;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

// Пузырь сообщения (кадр «Чаты», «тело сообщения»): белый r16, pad 10, gap 10,
// текст 14/18, время 12/16 #000 50 %; с карточкой товара — ширина 358.
function OffPlatformBanner() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div role="note" data-testid="chat-off-platform-banner" className="mx-4 mt-2.5 flex shrink-0 items-start gap-2 rounded-2xl bg-[#FFF1EC] p-2.5 text-[13px] leading-[18px] text-[#1F1F1F]"><ShieldAlert className="mt-px size-4 shrink-0 text-[#FA5C2E]" aria-hidden="true" /><p>Не переводите деньги и не уходите в мессенджеры - сделка вне OSMIX не защищена.</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function MessageBubble(t0) {
  const $ = _c(31);
  const {
    message
  } = t0;
  const mine = message.authorRole === "buyer";
  let t1;
  if ($[0] !== message.imageUris) {
    t1 = message.imageUris ?? [];
    $[0] = message.imageUris;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const images = t1;
  let t2;
  let t3;
  let t4;
  if ($[2] !== images || $[3] !== message.attachments || $[4] !== mine) {
    const products = (message.attachments ?? []).filter(_temp12);
    const t5 = mine ? "ml-auto items-end" : "mr-auto items-start";
    const t6 = products.length > 0 && "w-full";
    if ($[8] !== t5 || $[9] !== t6) {
      t2 = cn("flex max-w-[358px] flex-col gap-2.5 rounded-[16px] bg-white p-2.5", t5, t6);
      $[8] = t5;
      $[9] = t6;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    if ($[11] !== images || $[12] !== mine) {
      t3 = images.length > 0 && <div className={cn("flex flex-wrap gap-2", mine ? "justify-end" : "justify-start")}>{images.map(_temp13)}</div>;
      $[11] = images;
      $[12] = mine;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    t4 = products.map(_temp14);
    $[2] = images;
    $[3] = message.attachments;
    $[4] = mine;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  let t5;
  if ($[14] !== message.attachments) {
    t5 = message.attachments ?? [];
    $[14] = message.attachments;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== t5) {
    t6 = t5.map(_temp16);
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== message.body) {
    t7 = message.body && <p className="w-full text-[14px] leading-[18px] whitespace-pre-line break-words text-[#1F1F1F]">{message.body}</p>;
    $[18] = message.body;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== message.createdAt) {
    t8 = clock(message.createdAt);
    $[20] = message.createdAt;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t8) {
    t9 = <span className="text-[12px] leading-4 text-black/50">{t8}</span>;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t2 || $[25] !== t3 || $[26] !== t4 || $[27] !== t6 || $[28] !== t7 || $[29] !== t9) {
    t10 = <div className={t2}>{t3}{t4}{t6}{t7}{t9}</div>;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t6;
    $[28] = t7;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  return t10;
}
function _temp16(a_1, i) {
  return a_1.kind === "customization" && a_1.rows?.length ? <dl key={`rows-${i}`} className="grid w-full gap-1 rounded-[12px] bg-[#F8F8F8] p-2 text-[12px] leading-4 text-[#1F1F1F]" data-testid="chat-brief-rows">{a_1.rows.map(_temp15)}</dl> : null;
}
function _temp15(row) {
  return <div key={row.label} className="flex justify-between gap-3"><dt className="text-black/50">{row.label}</dt><dd className="text-right">{row.value}</dd></div>;
}
function _temp14(a_0) {
  return <ProductAttachmentCard key={a_0.productId} productId={a_0.productId ?? ""} name={a_0.productName} photo={a_0.productPhoto} price={a_0.priceLabel} />;
}
function _temp13(src) {
  return <a key={src} href={src} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-[4px]"><img src={src} alt="" className="h-[107px] w-20 object-cover" /></a>;
}
function _temp12(a) {
  return a.kind === "product" && a.productId;
}
function OfferBubble(t0) {
  const $ = _c(52);
  const {
    offer,
    onPay
  } = t0;
  const fmt = useFormatPrice();
  const [paying, setPaying] = useState(false);
  const status = offer.status === "accepted" ? offer.productId ? "\u0414\u043E\u0431\u0430\u0432\u043B\u0435\u043D\u043E \u0432 \u043A\u043E\u0440\u0437\u0438\u043D\u0443" : "\u041F\u0440\u0438\u043D\u044F\u0442\u043E - \u0436\u0434\u0451\u043C \u0441\u0447\u0451\u0442" : offer.status === "cancelled" ? "\u041F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u0435 \u043E\u0442\u043C\u0435\u043D\u0435\u043D\u043E" : offer.status === "expired" ? "\u0421\u0440\u043E\u043A \u043F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u044F \u0438\u0441\u0442\u0451\u043A" : null;
  let t1;
  if ($[0] !== offer.productPhoto) {
    t1 = offer.productPhoto ? <img src={offer.productPhoto} alt="" className="size-full object-cover" /> : null;
    $[0] = offer.productPhoto;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="h-[133px] w-[100px] shrink-0 overflow-hidden rounded-[16px] bg-white">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = offer.unitPrice * offer.quantity;
  let t4;
  if ($[4] !== fmt || $[5] !== t3) {
    t4 = fmt(t3);
    $[4] = fmt;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <p className="text-[14px] leading-5 font-bold text-[#333]">{t4}</p>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = offer.productName ?? "\u0418\u043D\u0434\u0438\u0432\u0438\u0434\u0443\u0430\u043B\u044C\u043D\u044B\u0439 \u0437\u0430\u043A\u0430\u0437";
  let t7;
  if ($[9] !== t6) {
    t7 = <p className="mt-[4px] truncate text-[15px] leading-5 font-medium text-[#1F1F1F]">{t6}</p>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== offer.description) {
    t8 = <p className="mt-[3px] line-clamp-2 text-[12px] leading-4 text-black/50">{offer.description}</p>;
    $[11] = offer.description;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== offer.productId || $[14] !== onPay || $[15] !== paying || $[16] !== status) {
    t9 = <div className="mt-auto pt-2">{status ? <span className="text-[12px] text-black/50">{status}</span> : <Button variant="accent" size="sm" className="rounded-[12px]" disabled={paying} onClick={() => {
        setPaying(true);
        onPay();
      }}>{offer.productId ? "\u041E\u043F\u043B\u0430\u0442\u0438\u0442\u044C" : "\u041F\u0440\u0438\u043D\u044F\u0442\u044C"}</Button>}</div>;
    $[13] = offer.productId;
    $[14] = onPay;
    $[15] = paying;
    $[16] = status;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t5 || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <div className="flex min-w-0 flex-1 flex-col pt-[3px] pr-6">{t5}{t7}{t8}{t9}</div>;
    $[18] = t5;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <PopoverTrigger asChild={true}><button type="button" aria-label={"\u0423\u0441\u043B\u043E\u0432\u0438\u044F \u043F\u0440\u0435\u0434\u043B\u043E\u0436\u0435\u043D\u0438\u044F"} className="absolute top-[10px] right-[10px] grid size-5 place-items-center rounded-full text-ds-muted outline-none hover:text-ds-ink-3 focus-visible:ring-2 focus-visible:ring-ds-focus"><InfoCircle20Icon size={20} /></button></PopoverTrigger>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = offer.quantity;
  let t13;
  if ($[24] !== fmt || $[25] !== offer.unitPrice) {
    t13 = fmt(offer.unitPrice);
    $[24] = fmt;
    $[25] = offer.unitPrice;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== offer.quantity || $[28] !== t13) {
    t14 = <p>Количество: {t12} × {t13}</p>;
    $[27] = offer.quantity;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== offer.productionEstimate) {
    t15 = offer.productionEstimate && <p>Срок изготовления: {offer.productionEstimate}</p>;
    $[30] = offer.productionEstimate;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== offer.selectedOptions) {
    t16 = offer.selectedOptions.length > 0 && <p>Параметры: {offer.selectedOptions.join(", ")}</p>;
    $[32] = offer.selectedOptions;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== offer.expiresAt) {
    t17 = offer.expiresAt && <p>Действует до {new Date(offer.expiresAt).toLocaleDateString("ru-RU")}</p>;
    $[34] = offer.expiresAt;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t14 || $[37] !== t15 || $[38] !== t16 || $[39] !== t17) {
    t18 = <Popover>{t11}<PopoverContent align="end" className="w-64 rounded-[12px] p-3 text-[13px] leading-5 text-[#1F1F1F]">{t14}{t15}{t16}{t17}</PopoverContent></Popover>;
    $[36] = t14;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t10 || $[42] !== t18 || $[43] !== t2) {
    t19 = <div className="relative flex gap-3 rounded-[20px] bg-[#F8F8F8] p-2">{t2}{t10}{t18}</div>;
    $[41] = t10;
    $[42] = t18;
    $[43] = t2;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== offer.createdAt) {
    t20 = clock(offer.createdAt);
    $[45] = offer.createdAt;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20) {
    t21 = <span className="mt-2 block text-[12px] leading-4 text-black/50">{t20}</span>;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t19 || $[50] !== t21) {
    t22 = <div className="mr-auto w-full max-w-[360px] rounded-[16px] bg-white p-[10px]" data-testid="chat-offer">{t19}{t21}</div>;
    $[49] = t19;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  return t22;
}
type Pending = {
  id: string;
  preview: string;
  url?: string;
  failed?: boolean;
};

// Вложение-товар над полем ввода (Айталы A-07). Кадра на него нет — собрано из
// карточки товара ленты (фото r8, цена 14 w700 #333, название 12/16 #797979)
// в высоту строки; крестик убирает товар из сообщения.
function ComposerProduct(t0) {
  const $ = _c(21);
  const {
    product,
    onRemove
  } = t0;
  const fmt = useFormatPrice();
  let t1;
  if ($[0] !== product.photos[0]) {
    t1 = product.photos[0] ? <img src={product.photos[0]} alt="" className="size-full object-cover" /> : null;
    $[0] = product.photos[0];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="h-[53px] w-10 shrink-0 overflow-hidden rounded-[8px] bg-white">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== fmt || $[5] !== product.price) {
    t3 = fmt(product.price);
    $[4] = fmt;
    $[5] = product.price;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <span className="text-[14px] leading-[14px] font-bold tracking-[-0.42px] text-[#333]">{t3}</span>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== product.name) {
    t5 = <span className="truncate text-[12px] leading-4 font-medium tracking-[-0.24px] text-[#797979]">{product.name}</span>;
    $[9] = product.name;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = <span className="flex min-w-0 flex-1 flex-col gap-1">{t4}{t5}</span>;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <XIcon size={20} />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== onRemove) {
    t8 = <button type="button" onClick={onRemove} aria-label={"\u0423\u0431\u0440\u0430\u0442\u044C \u0442\u043E\u0432\u0430\u0440 \u0438\u0437 \u0441\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u044F"} className="grid size-8 shrink-0 place-items-center rounded-full text-ds-primary outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ds-focus">{t7}</button>;
    $[15] = onRemove;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t6 || $[19] !== t8) {
    t9 = <div className="flex items-center gap-3 rounded-[16px] bg-[#F8F8F8] p-2" data-testid="chat-composer-product">{t2}{t6}{t8}</div>;
    $[17] = t2;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}

/*
 * Композер (кадр «Чаты», «сообщение»): полоса 64, pad 12, gap 10; «+» 24,
 * поле 40 r20 #1F1F1F 3 % pad 10/14, Manrope 14/18 w500, плейсхолдер
 * #1F1F1F 48 %; в фокусе — чёрная обводка, не оранжевая (Айталы A-06).
 * Кнопка отправки всегда активна и не бледнеет: пустое сообщение (ни текста,
 * ни фото, ни товара) просто не уходит.
 */
function Composer({
  onSend,
  disabled,
  attachment
}: {
  onSend: (body: string, imageUris: string[]) => Promise<void>;
  disabled?: boolean;
  attachment?: {
    product: Product;
    onRemove: () => void;
  };
}) {
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [photos, setPhotos] = useState<Pending[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const uploading = photos.some(p => !p.url && !p.failed);
  const ready = photos.filter(p_0 => p_0.url).map(p_1 => p_1.url as string);
  const hasContent = draft.trim().length > 0 || ready.length > 0 || Boolean(attachment);
  const pick = (files: FileList | null) => {
    if (!files) return;
    for (const file of Array.from(files).slice(0, 10 - photos.length)) {
      const id = Math.random().toString(36).slice(2);
      setPhotos(prev => [...prev, {
        id,
        preview: URL.createObjectURL(file)
      }]);
      api.buyer.uploadChatImage(file).then(({
        url
      }) => setPhotos(prev_0 => prev_0.map(p_2 => p_2.id === id ? {
        ...p_2,
        url
      } : p_2))).catch((e: unknown) => {
        setPhotos(prev_1 => prev_1.map(p_3 => p_3.id === id ? {
          ...p_3,
          failed: true
        } : p_3));
        toast.error(e instanceof Error ? e.message : "Не удалось загрузить фото");
      });
    }
  };
  const send = async () => {
    if (disabled || sending) return;
    if (uploading) {
      toast("Фото ещё загружаются — отправим, когда догрузятся");
      return;
    }
    if (!hasContent) {
      inputRef.current?.focus();
      return;
    }
    setSending(true);
    try {
      await onSend(draft.trim(), ready);
      setDraft("");
      setPhotos([]);
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось отправить");
    } finally {
      setSending(false);
    }
  };
  return <div className="shrink-0 border-t border-[#F8F8F8] bg-white">
			{(attachment || photos.length > 0) && <div className="flex flex-col gap-2 px-3 pt-3">
					{attachment && <ComposerProduct product={attachment.product} onRemove={attachment.onRemove} />}
					{photos.length > 0 && <div className="flex gap-2 overflow-x-auto">
							{photos.map(p_4 => <div key={p_4.id} className="relative h-[60px] w-[45px] shrink-0 overflow-hidden rounded-[4px] bg-[#F2F2F2]">
									<img src={p_4.preview} alt="" className={cn("size-full object-cover", (!p_4.url || p_4.failed) && "opacity-50")} />
									<button type="button" aria-label="Убрать фото" onClick={() => setPhotos(prev_2 => prev_2.filter(x => x.id !== p_4.id))} className="absolute top-0.5 right-0.5 grid size-4 place-items-center rounded-full bg-black/60 text-white">
										<XIcon size={12} />
									</button>
								</div>)}
						</div>}
				</div>}
			<div className="flex h-16 items-center gap-2.5 p-3">
				{/* Иконки макета: «plus 1» 24 и «Bold/Send» 24 (залитый самолётик). */}
				<button type="button" onClick={() => fileRef.current?.click()} aria-label="Прикрепить фото" className="grid size-6 shrink-0 place-items-center rounded-full text-ds-primary outline-none hover:text-ds-accent focus-visible:ring-2 focus-visible:ring-ds-focus">
					<PlusIcon size={24} />
				</button>
				<input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic" multiple hidden onChange={e_1 => {
        pick(e_1.target.files);
        e_1.target.value = "";
      }} />
				{/* Поле DS filled M: 40, r20, #1F1F1F 3 %, pad 14, 14/18 w500 — как
        «text-field» 659×40 в кадре; фокус — чёрная обводка. */}
				<Input ref={inputRef} value={draft} onChange={e_2 => setDraft(e_2.target.value)} onKeyDown={e_3 => {
        if (e_3.key === "Enter" && !e_3.shiftKey) {
          e_3.preventDefault();
          void send();
        }
      }} placeholder="Написать сообщение..." aria-label="Сообщение" maxLength={2000} fieldClassName="flex-1" />
				<button type="button" onClick={() => void send()} aria-label="Отправить" aria-busy={sending || undefined} data-testid="chat-send" className="grid size-6 shrink-0 place-items-center rounded-full text-ds-accent outline-none focus-visible:ring-2 focus-visible:ring-ds-focus">
					<SendIcon size={24} />
				</button>
			</div>
		</div>;
}